import { useParams, useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import { useGameState, useSetGameState } from '../hooks';
import { GameStateEditor } from '../components';

export function GameStateDetailPage() {
  const { keyword } = useParams<{ keyword: string }>();
  const navigate = useNavigate();
  const { gameState, loading, error, refresh } = useGameState(keyword || '');
  const { setGameState, saving, error: saveError } = useSetGameState();

  const handleSave = async (data: string, version: number) => {
    if (!keyword || !gameState) return;
    try {
      await setGameState(keyword, {
        dataType: gameState.dataType,
        data,
        version,
      });
      refresh();
    } catch {
      // Error already captured by useSetGameState hook
    }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-3xl mx-auto space-y-4">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error || !gameState) {
    return (
      <div className="p-6 max-w-3xl mx-auto space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/a/arche_dfe_game_state')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <Card className="border-destructive">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              {error ? 'Error' : 'Not Found'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              {error || `Game state not found for keyword: ${keyword}`}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-4">
      <Button variant="ghost" size="sm" onClick={() => navigate('/a/arche_dfe_game_state')}>
        <ArrowLeft className="h-4 w-4 mr-1" /> Back
      </Button>
      <GameStateEditor state={gameState} onSave={handleSave} saving={saving} error={saveError} />
    </div>
  );
}
